// Order customizations in Take Order — selection state, pricing preview, summary text,
// line identity and validation (take-order-modifiers spec, Component 2). Pure functions
// only: no React, no network, no module state.
//
// Mirrors admin-mobile-app/src/utils/modifierSelection.js. Keep the two byte-identical apart from this one header line, following the existing money.js / menuName.js / addableOrder.js / salesMetrics.js convention. A waiter on the tablet and a waiter on the phone must price and validate the same ticket the same way.
//
// WHY THIS DUPLICATES demo-app/src/pages/ProductDetailsPage.jsx INSTEAD OF IMPORTING IT.
// The storefront's rules live inside a React component in a different project, and R7.2
// forbids modifying `demo-app`. So the rules are stated twice, which is a genuine risk.
// Two things manage it: every function below cites the storefront line numbers it was
// derived from, and the test suite encodes the storefront's semantics as named cases
// (design Property 4). If the storefront changes, those citations are where to look.
//
// THE SERVER IS THE PRICING AUTHORITY (decision D4). `previewModifierCents` exists to show
// a waiter what a customization will cost; `pos_order_core.derive_modifier_cents` decides
// what it actually costs and ignores whatever the client sent. Anything here being wrong is
// a display bug, not a revenue bug. That split matters more for staff orders than for the
// storefront, because the guest never sees this screen and so cannot notice a wrong price.
//
// SELECTIONS ARE PLAIN OBJECTS OF ARRAYS, `{ [groupId]: string[] }`, not the Sets the
// storefront holds in component state. Arrays are what gets submitted to the API and what
// survives a JSON round-trip, and keeping one representation end to end removes the
// serialize step that the storefront needs (`serializeSelections`, ProductDetailsPage.jsx
// ~276-291) and the class of bug where the two forms disagree.

/** Selection modes, as stored on a group. */
export const SINGLE = 'single';
export const MULTIPLE = 'multiple';

/** Group types, as stored. Only INCLUDED and DRESSING get special treatment. */
export const INCLUDED = 'INCLUDED';
export const EXTRAS = 'EXTRAS';
export const DRESSING = 'DRESSING';

/** Separator between summary sections. Matches the storefront (ProductDetailsPage.jsx ~345). */
export const SUMMARY_SEPARATOR = ' • ';

// ---------------------------------------------------------------------------
// Coercion helpers. Every one of these mirrors a specific storefront expression;
// the whole point is that a malformed catalog row is read the same way on both.
// ---------------------------------------------------------------------------

const str = (v) => String(v == null ? '' : v).trim();

/** `Number.isFinite(Number(x)) ? Number(x) : 0` — ProductDetailsPage.jsx ~250-251. */
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Groups as an array, dropping non-objects and anything without an id. */
export function normalizeGroups(groups) {
  if (!Array.isArray(groups)) return [];
  return groups.filter((g) => g && typeof g === 'object' && !Array.isArray(g) && str(g.id));
}

/** Options as an array, dropping non-objects and anything without an id. */
function optionsOf(group) {
  const opts = group && Array.isArray(group.options) ? group.options : [];
  return opts.filter((o) => o && typeof o === 'object' && !Array.isArray(o) && str(o.id));
}

/**
 * The read-only facts about a group that every function below needs.
 *
 * NOTE THE selectionMode DEFAULT. Seeding, the summary and the option list all read
 * `String(g.selectionMode || 'multiple')` (ProductDetailsPage.jsx ~249, ~763), but
 * VALIDATION reads `String(g.selectionMode || '')` with no default (~437). For a group
 * that does not set the field the two differ: validation's `effMax` stays 0 (unbounded)
 * where a defaulted 'multiple' would also give 0 — so in practice they agree, because the
 * default is 'multiple' and 'multiple' is what the empty string behaves as. Defaulting
 * once here is therefore faithful, and the test suite pins it.
 */
function groupFacts(group) {
  const name = str(group.name);
  return {
    id: str(group.id),
    name,
    // 'Option' is validation's fallback (~444); the sheet renders 'Options' (~760). The
    // fallback used in a MESSAGE is the validation one, so error text matches the
    // storefront's wording exactly.
    errorName: name || 'Option',
    groupType: str(group.groupType).toUpperCase(),
    selectionMode: (str(group.selectionMode) || MULTIPLE).toLowerCase(),
    minSelections: num(group.minSelections),
    maxSelections: num(group.maxSelections),
    // A group literally named "Size" is special-cased by the storefront: it is force-seeded
    // and its pick is summarised as "Size: Large" rather than "Add: Large". Matching on the
    // display name is fragile, but it is the storefront's rule and diverging would make the
    // same product read differently online and in-store (~253, ~301).
    isSizeGroup: name.toLowerCase() === 'size',
    options: optionsOf(group),
  };
}

/**
 * Effective maximum selections. ProductDetailsPage.jsx ~440.
 *
 * `maxSelections` when set; otherwise 1 for a single-select and 0 (unbounded) for a
 * multi-select. 0 means "no ceiling", NOT "none allowed".
 */
function effectiveMax(facts) {
  if (facts.maxSelections > 0) return facts.maxSelections;
  return facts.selectionMode === SINGLE ? 1 : 0;
}

/** Selected ids for one group, as a de-duplicated array of non-empty strings. */
function selectedIds(selections, groupId) {
  const raw = selections && typeof selections === 'object' ? selections[groupId] : null;
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const v of raw) {
    const id = str(v);
    if (id && out.indexOf(id) === -1) out.push(id);
  }
  return out;
}

/**
 * Does a product need a customization step at all?
 *
 * R2.2: a product with no groups must stay a one-tap add. This is the gate for that, so
 * `customer2` — which configures no groups on any product — sees Take Order exactly as it
 * is today.
 */
export function hasModifiers(product) {
  const groups = product && (product.ModifierGroups || product.modifierGroups);
  return normalizeGroups(groups).length > 0;
}

/** A product's groups, normalized. Accepts either casing. */
export function groupsOf(product) {
  if (!product || typeof product !== 'object') return [];
  return normalizeGroups(product.ModifierGroups || product.modifierGroups);
}

// ---------------------------------------------------------------------------
// Seeding
// ---------------------------------------------------------------------------

/**
 * Initial selections for a group set. ProductDetailsPage.jsx ~226-274.
 *
 * Design Property 3: this must leave no required group unsatisfied unless the group is
 * unsatisfiable as configured (fewer options than `minSelections`). That is what makes the
 * common case one extra tap rather than a puzzle — a required single-select is pre-answered
 * with its default, or with its first option if it has no default.
 *
 * Rules, in the storefront's order:
 *   * `single` — take the first default-selected option; if there is none, take the FIRST
 *     option when the group is forced (named "Size", or `minSelections > 0`, or
 *     `maxSelections === 1`).
 *   * `multiple` — take every default-selected option. This is also how INCLUDED groups
 *     start out fully selected, so unselecting one produces "No onion".
 *
 * Returns `{ [groupId]: string[] }` with an entry for every group, including empty arrays,
 * so callers can distinguish "group exists, nothing chosen" from "unknown group".
 */
export function seedSelections(groups) {
  const out = {};
  for (const group of normalizeGroups(groups)) {
    const f = groupFacts(group);
    const defaultIds = f.options.filter((o) => Boolean(o.defaultSelected)).map((o) => str(o.id));
    if (f.selectionMode === SINGLE) {
      // ~264: `maxSel === 1` counts as forced here but NOT in the toggle handler (~776),
      // where forcing only covers Size and `minSel > 0`. So a `maxSelections: 1` group is
      // seeded with a pick that the waiter can then clear. Faithful to the storefront;
      // pinned by test rather than tidied, since tidying it would diverge the two channels.
      const forced = f.isSizeGroup || f.minSelections > 0 || f.maxSelections === 1;
      const first = f.options.length ? str(f.options[0].id) : '';
      const pick = defaultIds[0] || (forced ? first : '');
      out[f.id] = pick ? [pick] : [];
    } else {
      out[f.id] = defaultIds;
    }
  }
  return out;
}

/**
 * Re-seed only the groups a stored selection map does not already cover.
 *
 * Used when re-opening the sheet for a line already on the ticket: keep what the waiter
 * chose, fill in anything the menu has gained since. The storefront does the coarser thing
 * (~239-243: an existing selection map wins wholesale), which is fine there because a cart
 * item cannot outlive a menu change by long. Take Order tickets can, so an added group
 * would otherwise arrive unseeded and block the submit with no visible cause.
 */
export function mergeSelections(groups, selections) {
  const seeded = seedSelections(groups);
  const out = {};
  for (const group of normalizeGroups(groups)) {
    const gid = str(group.id);
    const existing = selections && typeof selections === 'object' ? selections[gid] : undefined;
    out[gid] = Array.isArray(existing) ? selectedIds(selections, gid) : seeded[gid];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Toggling
// ---------------------------------------------------------------------------

/**
 * Apply one tap. ProductDetailsPage.jsx ~769-805. Returns a NEW selection map; returns the
 * input unchanged when the tap is a no-op, so React can skip a render.
 *
 *   * `single` — tapping a different option replaces the pick. Tapping the current pick
 *     clears it, UNLESS the group is forced (named "Size", or `minSelections > 0`), in
 *     which case the tap is ignored: a required choice cannot be un-made, only changed.
 *   * `multiple` — tapping toggles. Once `Effective_Max` options are selected, further
 *     additions are IGNORED rather than rejected with a message, matching the storefront.
 *     The ceiling is therefore never reachable through the UI, which is why the "choose at
 *     most N" validation branch only fires on a payload the UI did not build.
 */
export function toggleSelection(groups, selections, groupId, optionId) {
  const gid = str(groupId);
  const oid = str(optionId);
  if (!gid || !oid) return selections;

  const group = normalizeGroups(groups).find((g) => str(g.id) === gid);
  if (!group) return selections;

  const f = groupFacts(group);
  const current = selectedIds(selections, gid);
  const has = current.indexOf(oid) !== -1;
  let next;

  if (f.selectionMode === SINGLE) {
    const forced = f.isSizeGroup || f.minSelections > 0;
    if (has) {
      if (forced) return selections;
      next = [];
    } else {
      next = [oid];
    }
  } else if (has) {
    next = current.filter((id) => id !== oid);
  } else {
    const max = effectiveMax(f);
    if (max > 0 && current.length >= max) return selections;
    next = current.concat([oid]);
  }

  return { ...(selections && typeof selections === 'object' ? selections : {}), [gid]: next };
}

// ---------------------------------------------------------------------------
// Pricing preview and summary text
// ---------------------------------------------------------------------------

/**
 * Summary text and price preview in one pass. ProductDetailsPage.jsx ~277-347.
 *
 * Kept as a single function because the storefront computes both in one loop and the two
 * must agree about which options counted: a summary listing "Add: Bacon" while the price
 * omitted it is worse than either error alone.
 *
 * Sections, in fixed order: INCLUDED removals, then `Size: X`, then `Add: A, B`, then
 * `Dressing: X`. Size and Dressing print only their FIRST pick (~342, ~344) even if the
 * group somehow allows several.
 *
 * PRICING RULES, all three load-bearing:
 *   * INCLUDED options never contribute, selected or not (~318). They are part of the
 *     advertised price; removing one is not a discount.
 *   * Only `priceDeltaCents > 0` contributes (~333). A zero or negative delta adds nothing,
 *     so "American cheese, no charge" prices as configured.
 *   * `Math.floor` on the delta (~309), so a fractional cent never rounds up in the
 *     waiter's favour.
 *
 * Output is ENGLISH ONLY, deliberately. It becomes `item_notes`, which is what the kitchen
 * chit and the receipt print, and prints stay English by owner request.
 */
export function summarizeSelections(groups, selections) {
  const includedRemovals = [];
  const sizePick = [];
  const extrasAdds = [];
  const dressingPick = [];
  let modifierPriceCents = 0;

  for (const group of normalizeGroups(groups)) {
    const f = groupFacts(group);
    const selected = selectedIds(selections, f.id);

    for (const opt of f.options) {
      const oid = str(opt.id);
      const isSelected = selected.indexOf(oid) !== -1;
      const isDefault = Boolean(opt.defaultSelected);
      const optName = str(opt.name);
      const delta = Math.floor(num(opt.priceDeltaCents));

      if (f.groupType === INCLUDED) {
        if (isDefault && !isSelected && optName) includedRemovals.push(`No ${optName}`);
        continue;
      }

      if (!isSelected) continue;

      if (f.isSizeGroup) {
        if (optName) sizePick.push(optName);
      } else if (f.groupType === DRESSING) {
        if (optName) dressingPick.push(optName);
      } else if (optName) {
        extrasAdds.push(optName);
      }

      if (delta > 0) modifierPriceCents += delta;
    }
  }

  const parts = [];
  if (includedRemovals.length) parts.push(includedRemovals.join(', '));
  if (sizePick.length) parts.push(`Size: ${sizePick[0]}`);
  if (extrasAdds.length) parts.push(`Add: ${extrasAdds.join(', ')}`);
  if (dressingPick.length) parts.push(`Dressing: ${dressingPick[0]}`);

  return { modifierSummary: parts.join(SUMMARY_SEPARATOR), modifierPriceCents };
}

/** Just the text. */
export function modifierSummary(groups, selections) {
  return summarizeSelections(groups, selections).modifierSummary;
}

/** Just the cents. ADVISORY — the server re-derives and discards this (D4). */
export function previewModifierCents(groups, selections) {
  return summarizeSelections(groups, selections).modifierPriceCents;
}

// ---------------------------------------------------------------------------
// Line identity
// ---------------------------------------------------------------------------

/**
 * Canonical identity for a ticket line (decision D2, design Property 8).
 *
 * Two "Any Cheese" with different cheeses are two lines; two with the same cheeses merge
 * and accumulate quantity, exactly as identical plain products do today. That requires an
 * identity that ignores the ORDER a waiter tapped things in, hence sorting both the group
 * ids and the option ids within each group.
 *
 * Groups with nothing selected are omitted, so `{}`, `{ g: [] }` and a missing map all key
 * the same line as a plain add. Without that, a line added before a group was configured
 * would refuse to merge with one added after.
 *
 * Format: `pid|gid:oid,oid|gid:oid`. Opaque; never parsed, never persisted, never shown.
 */
export function lineKey(productId, selections) {
  const pid = str(productId);
  const raw = selections && typeof selections === 'object' ? selections : {};
  const parts = [];
  for (const gid of Object.keys(raw).map(str).filter(Boolean).sort()) {
    const ids = selectedIds(raw, gid).slice().sort();
    if (ids.length) parts.push(`${gid}:${ids.join(',')}`);
  }
  return parts.length ? `${pid}|${parts.join('|')}` : pid;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * The answer to "what if the waiter doesn't choose". ProductDetailsPage.jsx ~431-455.
 *
 * Returns `{ ok, errors: [{ groupId, groupName, message }] }`. Structured rather than a
 * joined string because the sheet highlights each offending group inline and lists them in
 * a banner (R4.2-R4.6) — the storefront's `window.alert` is acceptable on a desktop browser
 * and wrong on a tablet held by someone mid-order.
 *
 * Rules, in the storefront's order so the first message matches:
 *   1. `minSelections >= 1` and fewer selected — "choose at least N"
 *   2. more than `Effective_Max` selected — "choose at most N"
 *   3. `single` with more than one selected — "choose one"
 *
 * All three can fire for one group; all are reported. A group can also be BOTH under its
 * min and over its max only if the config is contradictory, which is left to surface rather
 * than be smoothed over.
 *
 * COUNTS ARE NOT FILTERED against the group's option list here, matching the storefront.
 * An id that is not a real option still counts toward the minimum, so a stale client could
 * satisfy this check with a bogus id — which is precisely why the server re-validates
 * against the stored row and rejects unknown ids (design Property 5, R4.8). The client is
 * convenience; the server is enforcement.
 *
 * A product with groups and NO selection map at all validates as if every group were empty,
 * so a required group blocks. That is the row of the design's validation table that matters
 * most: it stops an old client or a replayed request from creating an ambiguous ticket.
 */
export function validateSelections(groups, selections) {
  const errors = [];
  for (const group of normalizeGroups(groups)) {
    const f = groupFacts(group);
    const count = selectedIds(selections, f.id).length;
    const max = effectiveMax(f);

    if (f.minSelections > 0 && count < f.minSelections) {
      errors.push({
        groupId: f.id,
        groupName: f.errorName,
        message: `${f.errorName}: choose at least ${f.minSelections}`,
      });
    }
    if (max > 0 && count > max) {
      errors.push({
        groupId: f.id,
        groupName: f.errorName,
        message: `${f.errorName}: choose at most ${max}`,
      });
    }
    if (f.selectionMode === SINGLE && count > 1) {
      errors.push({
        groupId: f.id,
        groupName: f.errorName,
        message: `${f.errorName}: choose one`,
      });
    }
  }
  return { ok: errors.length === 0, errors };
}

/** Group ids that fail validation — what the sheet outlines in red. */
export function unsatisfiedGroupIds(groups, selections) {
  const out = [];
  for (const err of validateSelections(groups, selections).errors) {
    if (out.indexOf(err.groupId) === -1) out.push(err.groupId);
  }
  return out;
}

/**
 * Drop groups that no longer exist and option ids the group no longer offers.
 *
 * Applied to a selection map before it is submitted, so a ticket built against a menu that
 * has since changed sends only ids the server will accept — the difference between a clear
 * "choose at least 1" and an opaque "unknown option" 400. Does not invent selections: if
 * pruning empties a required group, validation then reports it normally.
 */
export function pruneSelections(groups, selections) {
  const out = {};
  for (const group of normalizeGroups(groups)) {
    const f = groupFacts(group);
    const valid = f.options.map((o) => str(o.id));
    const kept = selectedIds(selections, f.id).filter((id) => valid.indexOf(id) !== -1);
    if (kept.length) out[f.id] = kept;
  }
  return out;
}

/**
 * Selection map shaped for the API, or `undefined` when there is nothing to send.
 *
 * `undefined` rather than `{}` is load-bearing: it is what keeps a plain product's request
 * body byte-identical to pre-feature, which is the whole of design Property 9 on the wire.
 * The server's "no selection map" branch is chosen by the key's absence.
 */
export function serializeSelections(selections) {
  const raw = selections && typeof selections === 'object' ? selections : {};
  const out = {};
  for (const gid of Object.keys(raw)) {
    const key = str(gid);
    if (!key) continue;
    const ids = selectedIds(raw, key);
    if (ids.length) out[key] = ids;
  }
  return Object.keys(out).length ? out : undefined;
}
